import { useQuery } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { apiClient } from '../api/client'

interface SidebarProps {
  open: boolean
}

// Map collection names/slugs to Font Awesome icons
const COLLECTION_ICONS: Record<string, string> = {
  'tscheppaschlucht': 'fa-mountain',
  'insecta': 'fa-microscope',
  'insecta-ffg-forte': 'fa-microscope',
  'agentos': 'fa-microchip',
  'arkturian': 'fa-bolt',
  'edera-safety': 'fa-shield-alt',
  'adic26-verteidigung': 'fa-shield-alt',
  'oneal-europe': 'fa-motorcycle',
  'tarot-karten': 'fa-star-of-david',
  'agents': 'fa-robot',
  'infrastruktur': 'fa-server',
  'arcturian-engine': 'fa-cogs',
  'content-system': 'fa-database',
  'room': 'fa-cube',
  'gps-tracker': 'fa-map-marker-alt',
}

function getCollectionIcon(slug: string): string {
  return COLLECTION_ICONS[slug] || 'fa-folder'
}

const DOC_TYPE_ICONS: Record<string, string> = {
  beitrag: 'fa-file-alt',
  docu: 'fa-file-alt',
  story: 'fa-book',
  angebot: 'fa-file-invoice',
  review: 'fa-star',
  survey: 'fa-poll',
  audio_guide: 'fa-headphones',
  agent: 'fa-robot',
  annotation: 'fa-search',
}

export function Sidebar({ open }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: collections = [] } = useQuery({
    queryKey: ['collections'],
    queryFn: () => apiClient.listCollections(),
    staleTime: 60_000,
  })

  const { data: docTypes = [] } = useQuery({
    queryKey: ['doc-types'],
    queryFn: () => apiClient.getDocTypes(),
    staleTime: 60_000,
  })

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiClient.getTags(),
    staleTime: 60_000,
  })

  // Only root collections (no parent)
  const rootCollections = collections.filter(c => !c.parent_id)

  const isHome = location.pathname === '/'

  const handleCollectionClick = (slug: string) => {
    navigate(`/?collection=${slug}`)
  }

  const handleDocTypeClick = (dt: string) => {
    navigate(`/?doc_type=${dt}`)
  }

  const handleTagClick = (tag: string) => {
    navigate(`/?tag=${tag}`)
  }

  return (
    <aside className={`sidebar ${open ? 'sidebar--open' : ''}`}>
      <div className="sidebar-section">
        <div className="sidebar-label">Collections</div>
        {rootCollections.map(c => (
          <button
            key={c.id}
            className={`sidebar-item ${location.search.includes(c.slug) ? 'sidebar-item--active' : ''}`}
            onClick={() => handleCollectionClick(c.slug)}
          >
            <span className="icon">
              <i className={`fas ${getCollectionIcon(c.slug)}`} />
            </span>
            {c.name}
            <span className="count">{c.post_count}</span>
          </button>
        ))}
        {isHome && (
          <button className="sidebar-item" onClick={() => navigate('/')} style={{ color: 'var(--accent)' }}>
            <span className="icon"><i className="fas fa-th-large" /></span>
            All Posts
          </button>
        )}
      </div>

      <div className="sidebar-section">
        <div className="sidebar-label">Content Types</div>
        {docTypes.map(dt => (
          <button
            key={dt}
            className={`sidebar-item ${location.search.includes(`doc_type=${dt}`) ? 'sidebar-item--active' : ''}`}
            onClick={() => handleDocTypeClick(dt)}
          >
            <span className="icon"><i className={`fas ${DOC_TYPE_ICONS[dt] || 'fa-file'}`} /></span>
            {dt}
          </button>
        ))}
      </div>

      <div className="sidebar-section">
        <div className="sidebar-label">Tags</div>
        <div>
          {tags.slice(0, 12).map(tag => (
            <span
              key={tag}
              className="sidebar-tag"
              onClick={() => handleTagClick(tag)}
            >
              <i className="fas fa-hashtag" style={{ fontSize: 9, marginRight: 2 }} /> {tag}
            </span>
          ))}
          {tags.length > 12 && (
            <span className="sidebar-tag" style={{ color: 'var(--text-muted)' }}>+{tags.length - 12} more</span>
          )}
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <div className="sidebar-section" style={{ borderBottom: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className="topbar-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>AP</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Alex Arkturian</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Admin</div>
        </div>
      </div>
    </aside>
  )
}
